import {
  Bell,
  CheckCheck,
  Gauge,
  HandCoins,
  Inbox,
  MessageSquare,
  MessageSquareHeart,
  TriangleAlert,
  UserCheck,
  type LucideIcon,
  Unplug,
} from 'lucide-react'
import { useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { Segmented } from '@/components/ui/segmented'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState, ErrorState } from '@/components/ui/states'
import { PageTitle } from '@/app/layout'
import { useMe } from '@/features/auth/api'
import { errorMessage } from '@/lib/api/errors'
import type { NotificationCategory, StaffNotification } from '@/lib/api/types'
import { cn } from '@/lib/cn'
import { numberFmt } from '@/lib/format'
import { useNow } from '@/lib/use-now'
import { useEmailPreference, useMarkRead, useNotificationList } from './api'

type CategoryFilter = NotificationCategory | 'all'

const categoryLabels: Record<NotificationCategory, string> = {
  support: 'Support',
  referrals: 'Referrals',
  feedback: 'Feedback',
  system: 'System',
}

/** What each kind of notification is, at a glance: its icon and colour. */
const KIND: Record<string, { icon: LucideIcon; tone: string; label: string }> = {
  'ticket.new': { icon: Inbox, tone: 'lime', label: 'New ticket' },
  'ticket.reply': { icon: MessageSquare, tone: '', label: 'Reply' },
  'ticket.assigned': { icon: UserCheck, tone: '', label: 'Given to you' },
  'referrals.payout_requested': { icon: HandCoins, tone: 'ok', label: 'Payout to send' },
  'feedback.new': { icon: MessageSquareHeart, tone: 'lime', label: 'New feedback' },
  service_limit: { icon: Gauge, tone: 'warn', label: 'Service limit' },
  'system.connections': { icon: Unplug, tone: 'bad', label: 'Connection errors' },
  system: { icon: TriangleAlert, tone: 'bad', label: 'System alert' },
}
const OTHER = { icon: Bell, tone: '', label: 'Notification' }

const dayFmt = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
const timeFmt = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' })

/** "Today", "Yesterday" or "Monday, October 12", by the browser's calendar. */
function dayLabel(iso: string, now: number) {
  const d = new Date(iso)
  const today = new Date(now)
  today.setHours(0, 0, 0, 0)
  const diff = Math.round((today.getTime() - new Date(d).setHours(0, 0, 0, 0)) / 86_400_000)
  return diff <= 0 ? 'Today' : diff === 1 ? 'Yesterday' : dayFmt.format(d)
}

/**
 * Support tickets, referral payouts, customer feedback and system alerts, newest first and grouped by day
 * (redesign of 2026-10-16). Also sent by email unless you turn that off.
 */
export function NotificationsPage() {
  const me = useMe()
  const now = useNow()
  const [params, setParams] = useSearchParams()
  const category = (params.get('category') as CategoryFilter | null) ?? 'all'
  const unreadOnly = params.get('show') === 'unread'
  const page = Number(params.get('page') ?? 1)
  const notifications = useNotificationList(category, unreadOnly, page)
  const read = useMarkRead()
  const email = useEmailPreference()
  const navigate = useNavigate()

  const counts = notifications.data?.meta.unread_by_category
  const unreadHere = category === 'all' ? (notifications.data?.meta.unread ?? 0) : (counts?.[category] ?? 0)
  const update = (next: Record<string, string>) =>
    setParams(
      (p) => {
        const merged = new URLSearchParams(p)
        for (const [k, v] of Object.entries(next)) {
          if (v === '') merged.delete(k)
          else merged.set(k, v)
        }
        if (!('page' in next)) merged.delete('page')
        return merged
      },
      { replace: true },
    )

  const days: { day: string; items: StaffNotification[] }[] = []
  for (const n of notifications.data?.data ?? []) {
    const day = dayLabel(n.created_at, now)
    const last = days.at(-1)
    if (last?.day === day) last.items.push(n)
    else days.push({ day, items: [n] })
  }
  const emailOn = me.data?.email_notifications ?? true

  return (
    <>
      <PageTitle
        title="Notifications"
        description="New tickets and website enquiries, customers’ replies and tickets given to you; referral payouts to send; service limits and alerts. Newest first."
        actions={
          unreadHere > 0 && (
            <button
              type="button"
              className="btn"
              disabled={read.isPending}
              onClick={() => read.mutate(category === 'all' ? {} : { category })}
            >
              <CheckCheck aria-hidden="true" />
              {category === 'all'
                ? 'Mark all as read'
                : `Mark ${categoryLabels[category].toLowerCase()} as read`}
            </button>
          )
        }
      />
      <div className="fbar">
        <Segmented<CategoryFilter>
          label="Category"
          value={category}
          options={[
            { value: 'all', label: 'All', count: notifications.data?.meta.unread || undefined },
            ...(Object.keys(categoryLabels) as NotificationCategory[]).map((c) => ({
              value: c,
              label: categoryLabels[c],
              count: counts?.[c] || undefined,
            })),
          ]}
          onChange={(v) => update({ category: v === 'all' ? '' : v })}
        />
        <Segmented
          label="Show"
          value={unreadOnly ? 'unread' : 'all'}
          options={[
            { value: 'all', label: 'Everything' },
            { value: 'unread', label: 'Unread only' },
          ]}
          onChange={(v) => update({ show: v === 'unread' ? 'unread' : '' })}
        />
        <span className="push" style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 }}>
          <span style={{ color: 'var(--muted)' }}>Also email me</span>
          <button
            type="button"
            role="switch"
            className="switch"
            aria-checked={emailOn}
            aria-label={`Also email me (${me.data?.email ?? 'your email'})`}
            title={me.data?.email}
            disabled={email.isPending}
            onClick={() =>
              email.mutate(!emailOn, {
                onSuccess: (s) =>
                  toast.success(
                    s.email_notifications
                      ? 'You’ll get these by email too.'
                      : 'Emails off. They still show here.',
                  ),
                onError: (err) => toast.error(errorMessage(err)),
              })
            }
          />
        </span>
      </div>

      {notifications.isPending ? (
        <Skeleton className="h-64 rounded-[14px]" />
      ) : notifications.isError ? (
        <div className="card">
          <ErrorState error={notifications.error} onRetry={() => void notifications.refetch()} />
        </div>
      ) : notifications.data.data.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={<Bell />}
            title={unreadOnly ? 'All caught up' : 'Nothing here yet'}
            description={
              category === 'all'
                ? 'New tickets, replies, payouts to send and system alerts show up here.'
                : `${categoryLabels[category]} notifications show up here.`
            }
          />
        </div>
      ) : (
        <div className="card pad">
          {days.map(({ day, items }) => (
            <section key={day} aria-label={day}>
              <h2 className="day-h">{day}</h2>
              <div className="feed">
                {items.map((n) => {
                  const k = KIND[n.kind] ?? OTHER
                  return (
                    <button
                      key={n.id}
                      type="button"
                      className={cn('fe', !n.read && 'unread')}
                      onClick={() => {
                        if (!n.read) read.mutate({ ids: [n.id] })
                        if (n.url) void navigate(n.url)
                      }}
                    >
                      <span className={cn('fe-ic', k.tone)}>
                        <k.icon aria-hidden="true" className="size-4" />
                      </span>
                      <span className="fe-t">
                        <b style={n.read ? { fontWeight: 500 } : undefined}>{n.title}</b>
                        {!n.read && <span className="sr-only"> (unread)</span>}
                        <small>
                          <span className="chip" style={{ height: 20, fontSize: 11 }}>
                            {k === OTHER ? categoryLabels[n.category] : k.label}
                          </span>
                          {n.body && <span className="line-clamp-2 whitespace-pre-line">{n.body}</span>}
                        </small>
                      </span>
                      <time className="fe-time" dateTime={n.created_at}>
                        {timeFmt.format(new Date(n.created_at))}
                      </time>
                    </button>
                  )
                })}
              </div>
            </section>
          ))}
          {notifications.data.meta.last_page > 1 && (
            <div className="t-foot" style={{ marginTop: 8 }}>
              <span>
                Page {notifications.data.meta.current_page} of {notifications.data.meta.last_page} ·{' '}
                {numberFmt.format(notifications.data.meta.total)} notifications
              </span>
              <span className="acts">
                <button
                  type="button"
                  className="btn sm ghost"
                  disabled={page <= 1}
                  onClick={() => update({ page: String(page - 1) })}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="btn sm ghost"
                  disabled={page >= notifications.data.meta.last_page}
                  onClick={() => update({ page: String(page + 1) })}
                >
                  Next
                </button>
              </span>
            </div>
          )}
        </div>
      )}
    </>
  )
}
